import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError, map, mergeMap } from 'rxjs/operators';
import { getCurrentUser } from 'src/app/core/services/functions.service';
import { getEndpoint } from 'src/app/core/services/functions.service';
import { ConceptoComisionService } from '../../ajustedevalores/services/concepto-comision.service';

export interface Bonificacion {
  id: number;
  periodo: string;
  c_concepto: string;
  c_vend: string;
  valor: number;
  observaciones?: string;
  estado: string;
  c_emp: string;
  usuario_creacion: string;
  fecha_creacion: string;
  usuario_modificacion?: string;
  fecha_modificacion?: string;
}

export interface ConceptoBonificacion {
  codigo: string;
  descripcion: string;
}

export interface ResultadoCargaBonificacion {
  periodo: string;
  c_vend: string;
  c_concepto: string;
  valor: number;
  estado: string;
  mensaje: string;
}

export interface BulkLoadResponse {
  registrosInsertados: number;
  registrosEliminados: number;
  registrosFallidos: ResultadoCargaBonificacion[];
  mensaje: string;
}

export interface ApiResponse<T> {
  status: string;
  message: string;
  data: T;
}

@Injectable({
  providedIn: 'root'
})

export class BonificacionesService {
  // private ENDPOINTBONIFICACIONES = 'http://localhost:8000/coms-bonificaciones';
  // private ENDPOINTCONCEPTO = 'http://localhost:8000/concepto-comision';
  ENDPOINTCONCEPTO = getEndpoint('coms_concepto_comision');
  ENDPOINTBONIFICACIONES = getEndpoint('coms_bonificaciones');
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient, private conceptoComisionService: ConceptoComisionService) { }

  getBonificaciones(company: string): Observable<Bonificacion[]> {
    console.log('URL Bonificaciones:', `${this.ENDPOINTBONIFICACIONES}/`);
    return this.http.get<ApiResponse<Bonificacion[]>>(`${this.ENDPOINTBONIFICACIONES}/`, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'company': company
      }),
      observe: 'response'
    }).pipe(
      map(response => {
        const body = response.body;
        
        if (body && body.hasOwnProperty('data')) {
          return body.data;
        }
        return Array.isArray(body) ? body : [];
      }),
      catchError((error: HttpErrorResponse) => {
        
        let errorMessage = 'Error al cargar las bonificaciones.';
        
        // Manejo específico para el error de Oracle
        if (error.error?.detail && error.error.detail.includes('cx_Oracle')) {
          errorMessage = 'Error de conexión con la base de datos. Por favor, contacte al administrador del sistema.';
        } else if (error.error?.detail) {
          errorMessage += ` Detalle: ${error.error.detail}`;
        } else if (error.error?.message) {
          errorMessage += ` Mensaje: ${error.error.message}`;
        } else if (typeof error.error === 'string') {
          errorMessage += ` Error: ${error.error}`;
        }

        if (error.status === 500) {
          errorMessage += ' Se ha producido un error interno en el servidor.';
        }

        return throwError(() => ({
          message: errorMessage,
          statusCode: error.status,
          error: error.error,
          isOracleError: error.error?.detail?.includes('cx_Oracle') || false
        }));
      })
    );
  }

  getBonificacion(id: number, company: string): Observable<Bonificacion> {
    return this.http.get<Bonificacion>(`${this.ENDPOINTBONIFICACIONES}/${id}`, {
      headers: { 'company': company }
    });
  }

  createBonificacion(bonificacion: Partial<Bonificacion>, company: string): Observable<Bonificacion> {
    
    const currentUser = getCurrentUser();
    
    // Mantener los nombres de campos originales que espera el backend
    const bonificacionCompleta = {
      periodo: bonificacion.periodo,
      c_concepto: bonificacion.c_concepto,
      c_vend: bonificacion.c_vend,
      valor: Number(bonificacion.valor),
      observaciones: bonificacion.observaciones || '',
      c_emp: currentUser.c_emp,
      estado: 'A',
      usuario_creacion: currentUser.usuario_aplicativo,
      usuario_modificacion: currentUser.usuario_aplicativo
    };

    return this.http.post<Bonificacion>(`${this.ENDPOINTBONIFICACIONES}/`, bonificacionCompleta, {
      headers: { 'company': company }
    })
    .pipe(
      catchError(error => {
       
        
        if (error.error) {
          if (typeof error.error === 'string') {
            try {
              const parsedError = JSON.parse(error.error);
            } catch (e) {
            }
          }
        }

        if (error.message) {

        }

        return throwError(() => error);
      })
    );
  }

  updateBonificacion(id: number, bonificacion: Partial<Bonificacion>, company: string): Observable<Bonificacion> {
    const currentUser = getCurrentUser();
    const bonificacionCompleta = {
      ...bonificacion,
      c_emp: currentUser.c_emp,
      estado: 'A'
    };
    return this.http.put<Bonificacion>(`${this.ENDPOINTBONIFICACIONES}/${id}`, bonificacionCompleta, {
      headers: { 'company': company }
    }).pipe(
    );
  }

  deleteBonificacion(id: number, company: string): Observable<void> {
    const currentUser = getCurrentUser();
    const bonificacionData = {
      estado: 'I',
      usuario_modificacion: currentUser.usuario_aplicativo,
      c_emp: currentUser.c_emp
    };
    return this.http.put<void>(`${this.ENDPOINTBONIFICACIONES}/${id}/inactivar`, bonificacionData, {
      headers: { 'company': company }
    }).pipe();
  }

  procesarArchivoCargaMasiva(formData: FormData, company: string): Observable<any> {
    return this.http.post(`${this.ENDPOINTBONIFICACIONES}/upload/`, formData, {
      headers: { 'company': company }
    });
  }

  bulkLoad(company: string, data: any[], replace: boolean = false, periodo?: string): Observable<BulkLoadResponse> {
    const currentUser = getCurrentUser();
    const usuario = currentUser?.usuario_aplicativo;
    const c_emp = currentUser?.c_emp;

    if (!usuario) {
      throw new Error('El usuario es requerido');
    }

    if (!c_emp) {
      throw new Error('No se encontró la empresa del usuario');
    }

    // // Asegurarnos que cada registro tenga los campos requeridos
    // const datosFormateados = data.map(row => ({
    //   periodo: row.periodo,
    //   c_vend: row.c_vend,
    //   c_concepto: row.c_concepto,
    //   valor: Number(row.valor),
    //   observaciones: row.observaciones || ''
    // }));

    const payload = {
      datos: data,
      usuario: usuario,
      c_emp: c_emp
    };

    const url = replace && periodo
      ? `${this.ENDPOINTBONIFICACIONES}/bulk-load-replace/${periodo}`
      : `${this.ENDPOINTBONIFICACIONES}/bulk-load/`;

    return this.http.post<BulkLoadResponse>(url, payload, {
      headers: { 
        'company': company,
        'Content-Type': 'application/json'
      }
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        let errorMessage = 'Ocurrió un error al procesar el archivo';

        if (error.error?.detail) {
          errorMessage = error.error.detail;
        }

        return throwError(() => new Error(errorMessage));
      })
    );
  }

  getConceptosBonificacion(company: string): Observable<ConceptoBonificacion[]> {
    return this.conceptoComisionService.getConceptosAjuste(company).pipe(
      map(conceptos => conceptos.map(concepto => ({
        codigo: concepto.codigo,
        descripcion: concepto.descripcion
      })))
    );
  }
}
